import React from 'react';
import {
  TableRow, TableCell, Box, Typography, Chip, IconButton,
  Checkbox, FormGroup, FormControlLabel, Tooltip, Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import { VideoItem, GroupType } from './types';
import UnifiedTagDisplay from '../../components/UnifiedTagDisplay';
import { calculateVideoStats } from './utils/videoStatsCalculator';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

export interface VideoTableRowProps {
  video: VideoItem;
  index: number;
  onPlay: (video: VideoItem) => void;
  onTagChange: (video: VideoItem, tag: string, isAdd: boolean) => void;
  onDelete: (videoId: string) => void;
  onShare: (video: VideoItem) => void;
  onZoneChange: (video: VideoItem, zone: string, checked: boolean) => void;
  onOpenGroupDialog: (video: VideoItem) => void;
  allGroups: GroupType[];
  ZONE_OPTIONS: { label: string; value: string }[];
  extractYouTubeId: (url: string) => string | null;
  formatViewCount: (count?: string) => string;
  formatDate: (dateStr?: string) => string;
  columns?: string[];
  isGuest?: boolean; // 新增：是否為訪客用戶
}

const VideoTableRow: React.FC<VideoTableRowProps> = React.memo(({
  video, index, onPlay, onTagChange, onDelete, onShare, extractYouTubeId, formatViewCount, formatDate,
  onZoneChange, onOpenGroupDialog, allGroups, ZONE_OPTIONS,
  columns = ['index', 'thumbnail', 'info', 'checkpoints', 'tags', 'zones', 'groups', 'delete', 'share'],
  isGuest = false
}) => {
  // 縮圖與 info 欄位共用
  const videoId = extractYouTubeId(video.path || '');
  const isYouTube = video.provider === 'youtube' || !!videoId;
  const fileName = video.path ? decodeURIComponent(video.path.split('/').pop() || '') : '未知檔案';
  const modifiedAt = video.modifiedAt ? new Date(video.modifiedAt).toLocaleString() : '-';

  const tags = Array.isArray(video.tags) ? video.tags : [];
  const groupIdsArr = Array.isArray(video.groupIds) ? video.groupIds : [];
  const isTeamChecked = groupIdsArr.length > 0;
  const sharedGroups = allGroups.filter(g => groupIdsArr.includes(g.id));

  const renderCellContent = (key: string) => {
    switch (key) {
      case 'index':
        return <TableCell data-col="index">{index}</TableCell>;
      case 'thumbnail':
        return (
          <TableCell data-col="thumbnail" sx={{ padding: 0, width: 180, minWidth: 180 }}>
            {video.playMode === 'MV' && video.thumbnail ? (
              <Box sx={{ 
                width: '100%',
                height: '100%',
                minHeight: 60,
                flexShrink: 0 
              }}>
                <LazyLoadImage
                  src={video.thumbnail}
                  alt={video.mvTitle || 'MV影片縮圖'}
                  effect="blur"
                  placeholder={<Skeleton variant="rectangular" width="100%" height={90} />}
                  style={{ width: '100%', height: 'auto', borderRadius: 0, cursor: 'pointer', display: 'block' }}
                  onClick={() => onPlay(video)}
                  onError={(e) => {
                    // 如果 YouTube 縮圖載入失敗，嘗試使用預設的 YouTube 縮圖
                    const target = e.currentTarget as HTMLImageElement;
                    const videoId = extractYouTubeId(video.path || '');
                    if (videoId) {
                      target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                    } else {
                      target.style.display = 'none';
                    }
                  }}
                />
              </Box>
            ) : isYouTube ? (
              <Box sx={{ 
                width: '100%',
                height: '100%',
                minHeight: 60,
                flexShrink: 0 
              }}>
                <LazyLoadImage
                  src={
                    // 優先使用 video.thumbnail（已更新的 YouTube 縮圖）
                    video.thumbnail ||
                    // 其次使用 YouTube API 提供的縮圖
                    video.youtubeInfo?.thumbnails?.high?.url ||
                    video.youtubeInfo?.thumbnails?.medium?.url ||
                    // 最後使用預設的 YouTube 縮圖
                    `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                  }
                  alt={video.title || video.youtubeInfo?.title || '影片縮圖'}
                  effect="blur"
                  placeholder={<Skeleton variant="rectangular" width="100%" height={90} />}
                  style={{ width: '100%', height: 'auto', borderRadius: 0, cursor: 'pointer', display: 'block' }}
                  onClick={() => onPlay(video)}
                  onError={(e) => {
                    // 如果縮圖載入失敗，嘗試其他 YouTube 縮圖格式或隱藏（靜默處理，不污染控制台）
                    const target = e.target as HTMLImageElement;
                    const currentSrc = target.src;
                    if (currentSrc.includes('hqdefault.jpg')) {
                      target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                    } else if (currentSrc.includes('mqdefault.jpg')) {
                      target.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
                    } else {
                      // 所有格式都失敗，靜默隱藏（避免 404 錯誤顯示在控制台）
                      target.style.display = 'none';
                      target.style.visibility = 'hidden';
                    }
                  }}
                />
              </Box>
            ) : video.thumbnail ? (
              <Box sx={{ 
                width: '100%',
                height: '100%',
                minHeight: 60,
                flexShrink: 0 
              }}>
                <LazyLoadImage
                  src={video.thumbnail}
                  alt={video.fileName || fileName || '預設縮圖'}
                  effect="blur"
                  placeholder={<Skeleton variant="rectangular" width="100%" height={90} />}
                  style={{ width: '100%', height: 'auto', borderRadius: 12, cursor: 'pointer', display: 'block' }}
                  onClick={() => onPlay(video)}
                  onError={e => (e.currentTarget.src = '/default-thumbnail.png')}
                />
              </Box>
            ) : (
              <Box sx={{
                width: '100%',
                height: '100%',
                minHeight: 60,
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
                onClick={() => onPlay(video)}
              >
                <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center' }}>
                  {video.playMode === 'MV' ? 'MV影片' : '無縮圖預覽'}
                </Typography>
              </Box>
            )}
          </TableCell>
        );
      case 'info':
        return (
          <TableCell data-col="info">
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '108px', gap: 0.2 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ lineHeight: 1.25, mb: 0.2, width: '100%', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', whiteSpace: 'normal', textAlign: 'left' }}
              >
                {video.playMode === 'MV' && video.mvTitle
                  ? video.mvTitle
                  : isYouTube
                    ? (video.youtubeInfo?.title || video.videoInfo?.title || video.title || videoId || '-')
                    : (video.fileName || fileName || '-')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.2, lineHeight: 1.3, textAlign: 'left' }} noWrap>
                {video.playMode === 'MV' 
                  ? `影片：${video.mvUrl ? '已記錄' : '未記錄'}`
                  : (() => {
                      const channelTitle = video.channelTitle || video.youtubeInfo?.channelTitle || video.videoInfo?.channelTitle || '-';
                      console.log('🔍 VideoTableRow 頻道調試:', {
                        videoId: video.videoId,
                        viewpointName: video.viewpointName,
                        channelTitle: video.channelTitle,
                        youtubeInfoChannelTitle: video.youtubeInfo?.channelTitle,
                        videoInfoChannelTitle: video.videoInfo?.channelTitle,
                        finalChannelTitle: channelTitle
                      });
                      return `頻道：${isYouTube ? channelTitle : '-'}`;
                    })()
                }
              </Typography>
              {/* 在手機模式下隱藏觀看次數 */}
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 0.2, 
                  lineHeight: 1.3, 
                  textAlign: 'left',
                  display: { xs: 'none', sm: 'block' } // 手機模式隱藏
                }}
              >
                {video.playMode === 'MV'
                  ? `關注度：${video.viewCount || 0}`
                  : (() => {
                      const viewCount = video.viewCount || video.youtubeInfo?.viewCount || video.videoInfo?.viewCount;
                      console.log('🔍 VideoTableRow 觀看次數調試:', {
                        videoId: video.videoId,
                        viewpointName: video.viewpointName,
                        viewCount: video.viewCount,
                        youtubeInfoViewCount: video.youtubeInfo?.viewCount,
                        videoInfoViewCount: video.videoInfo?.viewCount,
                        finalViewCount: viewCount
                      });
                      return `觀看次數：${isYouTube ? formatViewCount(viewCount) : '-'}`;
                    })()
                }
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, textAlign: 'left' }}>
                {video.playMode === 'MV'
                  ? <>發布：{video.createdAt ? formatDate(typeof video.createdAt === 'string' ? video.createdAt : video.createdAt.toString()) : '-'}</>
                  : isYouTube
                    ? (() => {
                        const publishedAt = video.publishedAt || video.youtubeInfo?.publishedAt || video.videoInfo?.publishedAt;
                        console.log('🔍 VideoTableRow 發布日期調試:', {
                          videoId: video.videoId,
                          viewpointName: video.viewpointName,
                          publishedAt: video.publishedAt,
                          youtubeInfoPublishedAt: video.youtubeInfo?.publishedAt,
                          videoInfoPublishedAt: video.videoInfo?.publishedAt,
                          finalPublishedAt: publishedAt
                        });
                        return <>發布：{formatDate(publishedAt)}</>;
                      })()
                    : <>
                        檔案長度：{video.duration ? `${video.duration}s` : '-'}<br />
                        上次播放：{video.lastPlayedTime ? `${video.lastPlayedTime.toFixed(1)}s` : '-'}<br />
                        建立時間：{video.createdAt ? new Date(video.createdAt).toLocaleString() : (video.modifiedAt ? new Date(video.modifiedAt).toLocaleString() : '-')}
                      </>
                }
              </Typography>
            </Box>
          </TableCell>
        );
      case 'checkpoints':
        return (
      <TableCell data-col="checkpoints">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          fontSize: 13, 
          lineHeight: 1.2,
          width: '100%',
          minWidth: 180
        }}>
          <span style={{ marginBottom: 6, color: Array.isArray(video.vbids) && video.vbids.length > 1 ? 'red' : undefined }}>
            重點數: {(() => {
              // 🔧 修復：使用統一的統計數據計算邏輯
              const stats = calculateVideoStats(video);
              return stats.checkpointCount;
            })()}
          </span>
          <span style={{ marginBottom: 6 }}>關注度：{(() => {
            const stats = calculateVideoStats(video);
            return stats.attentionCount;
          })()}</span>
          <span style={{ marginBottom: 6 }}>收藏數：{(() => {
            const stats = calculateVideoStats(video);
            return stats.favoriteCount;
          })()}</span>
          <span style={{ textAlign: 'left', width: '100%', marginBottom: 6 }}>分享者：{video.userId ? video.userId.slice(0, 8) : '-'}</span>
          {video.modifiedAt && (
            <>
              <span style={{ marginBottom: 6 }}>
                讀取：{new Date(video.modifiedAt).toLocaleDateString('zh-TW', { year: 'numeric', month: 'numeric', day: 'numeric' })}
              </span>
              <span style={{ marginBottom: video.provider ? 6 : 0 }}>
                時間：{new Date(video.modifiedAt).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </>
          )}
          {video.provider && (
            <span style={{ color: '#888', fontSize: 12, marginBottom: 0 }}>提供者：{video.provider}</span>
          )}
        </Box>
      </TableCell>
        );
      case 'tags':
        return (
          <TableCell data-col="tags">
            <UnifiedTagDisplay
              tags={tags}
              userTags={video.tags || []}
              maxRows={4}
              maxTagsPerRow={3}
              showAddButton={true}
              onAddTag={() => onTagChange(video, '', true)}
              onDeleteTag={(tag) => onTagChange(video, tag, false)}
              readOnly={false}
              variant="compact"
            />
          </TableCell>
        );
      case 'zones':
        return (
      <TableCell>
        <FormGroup sx={{ gap: '0px', alignItems: 'center' }}>
          {ZONE_OPTIONS.map(opt => {
            // 🔒 權限檢查：只有影片擁有者才能看到和操作公開按鈕
            const isOwner = video.userId === (video as any).currentUserId;
            const shouldShowOption = opt.value === 'community' ? isOwner : true;
            
            // 如果不應該顯示選項，返回 null
            if (!shouldShowOption) {
              return null;
            }
            
            return (
              <FormControlLabel
                key={`${video.id}-zone-${opt.value}-${Math.random().toString(36).substr(2, 9)}`}
                control={
                  opt.value === 'team' ? (
                    <Checkbox
                      checked={isTeamChecked}
                      onChange={() => onOpenGroupDialog(video)}
                      disabled={isGuest}
                      size="small"
                      sx={{ p: '2px' }}
                    />
                  ) : (
                    <Checkbox
                      checked={Array.isArray(video.zones) ? video.zones.includes(opt.value) : false}
                      onChange={e => onZoneChange(video, opt.value, e.target.checked)}
                      disabled={isGuest}
                      size="small"
                      sx={{ p: '2px' }}
                    />
                  )
                }
                label={<span style={{ fontSize: 13 }}>{opt.label}</span>}
                sx={{ mb: 0, alignItems: 'center', minHeight: 18, py: 0, width: '100%', justifyContent: 'center' }}
              />
            );
          })}
        </FormGroup>
      </TableCell>
        );
      case 'groups':
        return (
      <TableCell data-col="groups">
        {groupIdsArr.length === 0 || allGroups.length === 0 ? (
          '-' 
        ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
            {sharedGroups.map(g => (
                  <Chip key={g.id} label={g.name} size="small" sx={{ backgroundColor: '#f0f0f0' }} />
            ))}
          </Box>
        )}
      </TableCell>
        );
      case 'delete':
        return (
      <TableCell>
        <IconButton size="small" color="error" onClick={() => onDelete(video.id)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
        );
      case 'share':
        return (
      <TableCell>
        <IconButton size="small" color="primary" onClick={() => onShare(video)}>
          <ShareIcon />
        </IconButton>
      </TableCell>
        );
      default:
        return null;
    }
  };

  return (
    <TableRow sx={{ 
      minHeight: '60px', // 減少行高度
      borderBottom: '2px solid #d0d0d0', // 添加更明顯的分隔線
      '& .MuiTableCell-root': {
        verticalAlign: 'top', // 對齊到頂部
        paddingTop: '0',
        paddingBottom: '0',
        borderBottom: '2px solid #d0d0d0', // 保持更明顯的分隔線
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none'
      }
    }}>
      {columns.map(key => (
        <React.Fragment key={key}>
          {renderCellContent(key)}
        </React.Fragment>
      ))}
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // 🔧 性能優化：自定義比較邏輯，減少不必要的重新渲染
  // 只在關鍵數據變化時才重新渲染
  return (
    prevProps.video.id === nextProps.video.id &&
    prevProps.video.title === nextProps.video.title &&
    prevProps.video.vbids?.length === nextProps.video.vbids?.length &&
    prevProps.video.favoriteCount === nextProps.video.favoriteCount &&
    prevProps.video.tags?.length === nextProps.video.tags?.length &&
    prevProps.video.groupIds?.length === nextProps.video.groupIds?.length &&
    prevProps.index === nextProps.index
  );
});

export default VideoTableRow; 